/**
 * Reports for customer-portal administrators.
 *
 * WHY THIS EXISTS
 * Requested: "The customer portal for admin users should also be able to run
 * some of the basic reports."
 *
 * Deliberately NOT the staff report builder in a different skin. A client
 * administrator wants "give me the user list" and "what licences are we paying
 * for", answered in one click and exportable. They do not want a column picker,
 * a filter DSL, saved definitions or scheduling — offering those would make a
 * simple question into a form to fill in, and the saved objects would then need
 * managing by somebody.
 *
 * So: pick a report, see it, download it. Columns come from the server's
 * default set for that type. Filtering is a client-side text search over the
 * rows already fetched, which is the interaction people expect from a table on
 * screen and costs the appliance nothing.
 *
 * SCOPE IS NOT A UI CONCERN. Every call is scoped to the caller's own customer
 * by the server, from the token. There is nothing on this page that could widen
 * it, which is the only way that guarantee is worth anything.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import portalApi, { portalDownload } from "../../api/portalClient";

type ColumnDef = { key: string; label: string; type: string };
type TypeDef = {
  key: string;
  label: string;
  description: string;
  category: string;
  default_columns: string[];
  columns: ColumnDef[];
};
/** A generated document (a PDF), as opposed to a table of rows. */
type DocDef = { key: string; label: string; description: string; formats: string[] };

type RunResult = {
  columns: string[];
  rows: Record<string, unknown>[];
  total_rows: number;
  capped: boolean;
  row_limit: number;
};

const CATEGORY_ORDER = ["Devices", "Users & licences", "Operations", "Service delivery"];

export default function PortalReports() {
  const [types, setTypes] = useState<TypeDef[]>([]);
  const [docs, setDocs] = useState<DocDef[]>([]);
  const [active, setActive] = useState<TypeDef | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    portalApi
      .get("/api/v1/portal/reports/types")
      .then((r) => setTypes(r.data || []))
      .catch((e) =>
        setError(e?.response?.data?.detail || "Could not load the list of reports"),
      )
      .finally(() => setLoading(false));
  }, []);

  // Generated documents. A failure here must not take the tabular reports
  // down with it — those are the common case.
  useEffect(() => {
    portalApi
      .get("/api/v1/portal/reports/documents")
      .then((r) => setDocs(r.data || []))
      .catch(() => setDocs([]));
  }, []);

  const downloadDocument = async (d: DocDef, fmt: string) => {
    setError("");
    try {
      // No customer_id in the request: the server takes it from the portal
      // token, so this can only ever produce your own company's document.
      await portalDownload(
        `/api/v1/portal/reports/documents/${d.key}/download?fmt=${fmt}`,
        `${d.label.replace(/[^A-Za-z0-9._-]+/g, "_")}.${fmt}`,
      );
    } catch (e: any) {
      setError(e?.message || "Could not generate that document");
    }
  };

  const grouped = useMemo(() => {
    const buckets = new Map<string, TypeDef[]>();
    for (const t of types) {
      const c = t.category || "General";
      if (!buckets.has(c)) buckets.set(c, []);
      buckets.get(c)!.push(t);
    }
    return [...buckets.entries()].sort(([a], [b]) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [types]);

  const run = useCallback(async (t: TypeDef) => {
    setActive(t);
    setRunning(true);
    setError("");
    setResult(null);
    setSearch("");
    setSortKey(null);
    try {
      const r = await portalApi.post("/api/v1/portal/reports/run", {
        report_type: t.key,
        columns: t.default_columns,
        filters: [],
        order_by: null,
        row_limit: 1000,
      });
      setResult(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not run that report");
    } finally {
      setRunning(false);
    }
  }, []);

  const download = async (fmt: "csv" | "pdf") => {
    if (!active) return;
    setError("");
    try {
      await portalDownload(
        `/api/v1/portal/reports/download.${fmt}`,
        `${active.label.replace(/[^A-Za-z0-9._-]+/g, "_")}.${fmt}`,
        {
          report_type: active.key,
          columns: active.default_columns,
          filters: [],
          order_by: null,
          row_limit: 1000,
        },
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not prepare that download");
    }
  };

  const label = (key: string) =>
    active?.columns.find((c) => c.key === key)?.label || key;

  const cell = (v: unknown) => {
    if (v === null || v === undefined || v === "") return null;
    // Booleans as Yes/No: a table of "true"/"false" reads like a debug dump.
    if (typeof v === "boolean") return v ? "Yes" : "No";
    // The API returns zone-less UTC; rendering it raw shows an ISO string with
    // a T in the middle, which nobody reads as a date.
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(v) ? v : v + "Z");
      if (!isNaN(d.getTime())) return d.toLocaleString();
    }
    return String(v);
  };

  const visibleRows = useMemo(() => {
    if (!result) return [];
    const q = search.trim().toLowerCase();
    let rows = result.rows;
    if (q) {
      rows = rows.filter((r) =>
        result.columns.some((c) => String(r[c] ?? "").toLowerCase().includes(q)),
      );
    }
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        // Blanks always last, whichever direction — an empty cell is not a
        // small value, and sorting it as one buries the rows that have data.
        const ae = av === null || av === undefined || av === "";
        const be = bv === null || bv === undefined || bv === "";
        if (ae && be) return 0;
        if (ae) return 1;
        if (be) return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
      });
    }
    return rows;
  }, [result, search, sortKey, sortDir]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Reports</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Pick a report to run it against your organisation. Everything here can be downloaded as a
        spreadsheet or a PDF.
      </p>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-gray-400">Loading reports…</p>}

      {!loading && types.length === 0 && docs.length === 0 && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No reports are available for your organisation yet.
        </p>
      )}

      {/* The documents a client is actually handed — QBR, assessment,
          inventory. Rendered for THEIR company: the server takes the customer
          from the portal token and does not accept one from the request. */}
      {docs.length > 0 && (
        <div className="mb-6">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
            Documents
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {docs.map((d) => (
              <div
                key={d.key}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
              >
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100 leading-snug">
                  {d.label}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                  {d.description}
                </p>
                <div className="mt-2 flex gap-1.5">
                  {d.formats.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => downloadDocument(d, f)}
                      className="text-xs font-medium px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:brightness-95"
                    >
                      {f.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {grouped.map(([category, items]) => (
        <div key={category} className="mb-5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
            {category}
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <button
                key={t.key}
                type="button"
                disabled={running}
                onClick={() => run(t)}
                className={`text-left rounded-xl border p-3 transition-all disabled:opacity-60 disabled:cursor-wait ${
                  active?.key === t.key
                    ? "border-blue-500 ring-1 ring-blue-500/30 bg-blue-50/50 dark:bg-blue-500/10 dark:border-blue-500"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-400 hover:shadow-sm dark:hover:border-blue-500"
                }`}
              >
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100 leading-snug">
                  {t.label}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-3">
                  {t.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      ))}

      {running && <p className="mt-6 text-sm text-gray-400">Running {active?.label}…</p>}

      {result && active && !running && (
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
              {active.label}
            </h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {visibleRows.length === result.total_rows
                ? `${result.total_rows} row${result.total_rows === 1 ? "" : "s"}`
                : `${visibleRows.length} of ${result.total_rows} rows`}
              {result.capped && (
                <span className="ml-2 text-amber-700 dark:text-amber-300">
                  · showing the first {result.row_limit}
                </span>
              )}
            </span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter…"
              className="ml-auto border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-48"
            />
            <button
              onClick={() => download("csv")}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              CSV
            </button>
            <button
              onClick={() => download("pdf")}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              PDF
            </button>
          </div>

          {result.rows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
              Nothing to show for this report yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                  <tr>
                    {result.columns.map((c) => (
                      <th
                        key={c}
                        onClick={() => toggleSort(c)}
                        className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                        title="Sort by this column"
                      >
                        {label(c)}
                        <span className="ml-1 text-gray-400">
                          {sortKey === c ? (sortDir === "asc" ? "↑" : "↓") : ""}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {visibleRows.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      {result.columns.map((c) => {
                        const v = cell(row[c]);
                        return (
                          <td
                            key={c}
                            className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300"
                          >
                            {v === null ? (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            ) : (
                              v
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
